import React from 'react';
import { Blend, ChevronDown, Search, type LucideIcon } from 'lucide-react';

type SearchFilterControlProps = {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  toggleActive: boolean;
  onToggleFilters: () => void;
  inputTestId: string;
  toggleTestId: string;
  className?: string;
};

type ActionIconButtonProps = {
  icon: LucideIcon;
  label: string;
  showLabel?: boolean;
  variant?: 'soft' | 'outline' | 'primary';
  compact?: boolean;
  compactPaddingClassName?: string;
  dataTestId?: string;
  ariaLabel?: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  className?: string;
};

type SegmentedIconOption<T extends string> = {
  value: T;
  icon: LucideIcon;
  label: string;
  testId: string;
};

type SegmentedIconToggleProps<T extends string> = {
  value: T;
  options: Array<SegmentedIconOption<T>>;
  onChange: (value: T) => void;
};

type InlineSelectControlProps = {
  value: string;
  options: string[];
  onChange: (next: string) => void;
  dataTestId: string;
  compact: boolean;
  className?: string;
  optionLabel?: (value: string) => string;
  compactClassName?: string;
};

const actionButtonVariantClassName: Record<NonNullable<ActionIconButtonProps['variant']>, string> = {
  soft: 'bg-primary/5 text-primary hover:text-primary/80',
  outline: 'border border-gray-200 bg-white text-irongrey hover:bg-gray-50',
  primary: 'bg-primary text-white hover:bg-primary-hover shadow-lg shadow-primary/20',
};

export const SearchFilterControl = ({
  value,
  onChange,
  placeholder,
  toggleActive,
  onToggleFilters,
  inputTestId,
  toggleTestId,
  className = 'w-full max-w-[360px] min-w-0',
}: SearchFilterControlProps) => (
  <section className={`flex items-center gap-2 rounded-2xl border border-gray-100 bg-white px-3 py-1.5 shadow-sm ${className}`}>
    <Search size={18} className="text-irongrey/40" />
    <input
      data-testid={inputTestId}
      className="min-w-0 flex-1 border-none bg-transparent py-1 text-sm font-medium text-irongrey placeholder:text-gray-400 focus:ring-0"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
    />
    <button
      type="button"
      data-testid={toggleTestId}
      onClick={onToggleFilters}
      className={`cursor-pointer rounded-xl p-1.5 transition-all duration-300 ${
        toggleActive ? 'bg-primary/10 text-primary' : 'bg-[#f4fefc] text-primary'
      }`}
      title="filter-button"
      aria-label="Toggle filters"
    >
      <Blend size={18} className={`transition-transform duration-300 ${toggleActive ? 'rotate-90' : 'rotate-0'}`} />
    </button>
  </section>
);

export const ActionIconButton = ({
  icon: Icon,
  label,
  showLabel = true,
  variant = 'soft',
  compact = false,
  compactPaddingClassName = 'px-2.5',
  dataTestId,
  ariaLabel,
  onClick,
  type = 'button',
  disabled = false,
  className = '',
}: ActionIconButtonProps) => (
  <button
    type={type}
    data-testid={dataTestId}
    aria-label={ariaLabel ?? label}
    disabled={disabled}
    onClick={onClick}
    className={`inline-flex shrink-0 items-center gap-2 rounded-lg py-2 text-sm font-medium transition-all disabled:cursor-not-allowed ${
      actionButtonVariantClassName[variant]
    } ${compact ? compactPaddingClassName : 'px-3'} ${className}`}
  >
    <Icon size={18} />
    <span className={showLabel ? 'inline' : 'sr-only'}>{label}</span>
  </button>
);

export const SegmentedIconToggle = <T extends string>({
  value,
  options,
  onChange,
}: SegmentedIconToggleProps<T>) => (
  <div className="flex rounded-lg border border-gray-100 bg-white p-1 shadow-sm">
    {options.map((option) => {
      const OptionIcon = option.icon;
      const isActive = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          data-testid={option.testId}
          className={`cursor-pointer rounded-md px-3 py-2 transition-all ${
            isActive ? 'bg-primary/10 text-primary shadow-sm' : 'text-irongrey/40 hover:bg-gray-50'
          }`}
          onClick={() => onChange(option.value)}
          aria-label={option.label}
        >
          <OptionIcon size={20} />
        </button>
      );
    })}
  </div>
);

export const InlineSelectControl = ({
  value,
  options,
  onChange,
  dataTestId,
  compact,
  className = '',
  optionLabel,
  compactClassName = 'px-2.5 max-w-[8.5rem]',
}: InlineSelectControlProps) => (
  <div className={`relative ${className}`}>
    <select
      data-testid={dataTestId}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={`appearance-none rounded-lg bg-primary/5 py-2 pr-8 text-sm font-medium text-primary ${
        compact ? compactClassName : 'px-3'
      }`}
    >
      {options.map((option) => (
        <option key={option} value={option}>{optionLabel ? optionLabel(option) : option}</option>
      ))}
    </select>
    <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-primary/70" />
  </div>
);

// TODO(ystnj-admissions-toolbar-migration): migrate admissions dashboard/detail top controls to these
// primitives after students parity gates pass in:
// - apps/ystnj/src/internal-owned/school-admissions-v2-1/pages/Dashboard.tsx
// - apps/ystnj/src/internal-owned/school-admissions-v2-1/pages/ApplicantDetail.tsx
